import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { downloadReport } from "../lib/nativeDownload";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { money, fmtDateAU } from "../lib/format";
import { toast } from "sonner";
import { FileDown, FileText, Send, UserPlus, Mail, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const PERIODS = [
  { v: "financial_year", l: "Current financial year" },
  { v: "previous_financial_year", l: "Previous financial year" },
  { v: "quarter", l: "Current quarter" },
  { v: "previous_quarter", l: "Previous quarter" },
  { v: "month", l: "Month" },
  { v: "custom", l: "Custom date range" },
];

export default function Accountant() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [period, setPeriod] = useState("financial_year");
  const ref = "2026-09-15";
  const [range, setRange] = useState({ start: "2026-07-01", end: "2027-06-30" });
  const [sendOpen, setSendOpen] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ["accountant-profile"],
    queryFn: async () => (await api.get(`/accountant/profile`)).data,
  });
  const [prof, setProf] = useState(null);
  const p = prof ?? (profile || { name: "", firm: "", email: "", phone: "", notes: "" });
  const setP = (k, v) => setProf({ ...p, [k]: v });

  const qs = () => {
    if (period === "custom") return `period=custom&start=${range.start}&end=${range.end}`;
    return `period=${period}&ref=${ref}`;
  };

  const { data: pack } = useQuery({
    queryKey: ["pack", period, range.start, range.end],
    queryFn: async () => (await api.get(`/accountant/pack?${qs()}`)).data,
  });

  const saveProfile = async () => {
    try {
      await api.put("/accountant/profile", {
        name: p.name || null, firm: p.firm || null, email: p.email || null,
        phone: p.phone || null, notes: p.notes || null,
      });
      qc.invalidateQueries({ queryKey: ["accountant-profile"] });
      toast.success("Accountant details saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const download = async (kind) => {
    try {
      const period = pack?.period?.label?.replace(/[^a-zA-Z0-9]+/g, "_") || "records";
      const uname = (pack?.owner_name || "Molaroo").replace(/[^a-zA-Z0-9]+/g, "_");
      const filename = `${uname}_${period}_Accountant_Pack.${kind}`;
      const mimeType = kind === "pdf" ? "application/pdf" : "text/csv";
      const { outcome } = await downloadReport({ path: `/accountant/pack.${kind}?${qs()}`, filename, mimeType });
      if (outcome === "cancelled") return;
      toast.success(outcome === "shared" ? `${kind.toUpperCase()} ready to save or share` : `${kind.toUpperCase()} downloaded`);
    } catch { toast.error("Could not generate file"); }
  };

  const sendViaMailto = () => {
    const fy = pack?.period?.label || "";
    const subj = encodeURIComponent(`Molaroo Records — ${fy}`);
    const body = encodeURIComponent(
      `Hi ${p.name || "there"},\n\nAttached are my Molaroo records for ${fy}.\n\n` +
      `The pack contains my recorded income, payments, practice arrangements and reconciliation information for the period.\n\n` +
      `Please review the information and let me know if you require anything further.\n\nRegards,\n${pack?.owner_name || ""}`
    );
    window.location.href = `mailto:${p.email || ""}?subject=${subj}&body=${body}`;
  };

  const openSend = () => {
    setRecipient(p.email || "");
    setSubject(`Molaroo Accountant Pack — ${pack?.owner_name || ""} — ${pack?.period?.label || ""}`);
    setSendOpen(true);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const payload = { recipient: recipient.trim(), subject: subject.trim(), period };
      if (period === "custom") { payload.start = range.start; payload.end = range.end; }
      else { payload.ref = ref; }
      const { data } = await api.post("/accountant/send", payload);
      toast.success(`Accountant Pack sent to ${data.recipient}`);
      setSendOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    setSending(false);
  };

  const s = pack?.summary;
  const fbase = `${(pack?.owner_name || "Molaroo").replace(/[^a-zA-Z0-9]+/g, "_")}_${(pack?.period?.label || "records").replace(/[^a-zA-Z0-9]+/g, "_")}_Accountant_Pack`;

  return (
    <AppShell>
      <PageHeader title="Accountant Pack" onBack={() => navigate(-1)} />
      <div className="px-5 pb-28 df-fade-in space-y-4">
        {/* Period */}
        <div className="df-card p-4">
          <Label className="text-slate-700">Reporting period</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="pack-period" className="mt-1.5 h-12 rounded-xl bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          {period === "custom" && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><Label className="text-xs">From</Label><Input type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} className="mt-1 h-11 rounded-xl" /></div>
              <div><Label className="text-xs">To</Label><Input type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} className="mt-1 h-11 rounded-xl" /></div>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="df-card p-5" data-testid="pack-preview">
          <div className="text-xs font-semibold tracking-wider uppercase text-[#0D786A]">{pack?.period?.label}</div>
          <div className="text-xs text-slate-500 mb-3">{pack ? `${pack.period.start_fmt} – ${pack.period.end_fmt}` : ""}</div>
          <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-3">
            <Stat label="Expected earnings" value={money(s?.expected_cents)} />
            <Stat label="Payments received" value={money(s?.received_cents)} tone="text-emerald-700" />
            <Stat label="Still to receive" value={money(s?.outstanding_cents)} tone="text-indigo-700" />
            <Stat label="Adjustments" value={money(s?.adjustments_cents)} />
          </div>
          <div className="text-xs text-slate-500 mt-3">{s?.practice_count || 0} practice(s) · {pack?.summary?.report_count || 0} payment report(s)</div>

          {pack?.income_by_practice?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
              {pack.income_by_practice.map((it, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 truncate">{it.name}</span>
                  <span className="font-num font-medium text-slate-900">{money(it.expected_cents)}</span>
                </div>
              ))}
            </div>
          )}
          {pack?.gst && (
            <div className="mt-4 pt-3 border-t border-slate-100" data-testid="pack-gst-summary">
              <div className="text-xs font-semibold tracking-wider uppercase text-[#0D786A] mb-2">GST / BAS summary</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Stat label="G1 Total sales" value={money(pack.gst.g1_total_sales_cents)} />
                <Stat label="1A GST on sales" value={money(pack.gst.a1_gst_on_sales_cents)} />
                <Stat label="1B GST on purchases" value={money(pack.gst.b1_gst_on_purchases_cents)} />
              </div>
              <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">{pack.gst.note}</p>
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">{pack?.disclaimer}</p>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button data-testid="download-pdf" onClick={() => download("pdf")} className="h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">
            <FileText size={17} className="mr-1.5" /> Download PDF
          </Button>
          <Button data-testid="download-csv" variant="outline" onClick={() => download("csv")} className="h-12 rounded-xl">
            <FileDown size={17} className="mr-1.5" /> Download CSV
          </Button>
        </div>
        <Button data-testid="generate-accountant-pack-button" onClick={openSend} className="w-full h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">
          <Send size={17} className="mr-1.5" /> Send to Accountant
        </Button>
        <button data-testid="send-mailto-fallback" onClick={sendViaMailto} className="w-full text-xs text-slate-400 df-pressable inline-flex items-center justify-center gap-1">
          <Mail size={12} /> Prefer to use your own email app? Open it instead
        </button>

        {/* Accountant details */}
        <div className="df-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus size={16} className="text-[#0D786A]" />
            <span className="text-sm font-semibold text-slate-900">Accountant details (optional)</span>
          </div>
          {!profile && !prof && (
            <p className="text-xs text-slate-500 mb-3">You can generate your Accountant Pack now or add your accountant's details to make sharing easier.</p>
          )}
          <div className="space-y-3">
            <div><Label className="text-slate-700">Name</Label><Input data-testid="acc-name" value={p.name || ""} onChange={(e) => setP("name", e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white" /></div>
            <div><Label className="text-slate-700">Firm</Label><Input data-testid="acc-firm" value={p.firm || ""} onChange={(e) => setP("firm", e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-700">Email</Label><Input data-testid="acc-email" value={p.email || ""} onChange={(e) => setP("email", e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white" /></div>
              <div><Label className="text-slate-700">Phone</Label><Input data-testid="acc-phone" value={p.phone || ""} onChange={(e) => setP("phone", e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white" /></div>
            </div>
            <div><Label className="text-slate-700">Notes</Label><Textarea data-testid="acc-notes" value={p.notes || ""} onChange={(e) => setP("notes", e.target.value)} className="mt-1.5 rounded-xl bg-white" /></div>
            <Button data-testid="acc-save" onClick={saveProfile} className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800">Save Accountant Details</Button>
          </div>
        </div>
      </div>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent data-testid="send-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send to Accountant</DialogTitle>
            <DialogDescription>
              Review the details below. Your Accountant Pack is generated securely on Molaroo's server and emailed with the PDF and CSV attached.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Row label="Reporting period" value={pack?.period?.label || ""} />
            <div>
              <Label className="text-slate-700 text-sm">Recipient</Label>
              <Input data-testid="send-recipient" type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="accountant@example.com" className="mt-1 h-11 rounded-xl bg-white" />
              <p className="text-[11px] text-slate-400 mt-1">Editing this only affects this send — it won't change your saved accountant details.</p>
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Subject</Label>
              <Input data-testid="send-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 h-11 rounded-xl bg-white" />
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-1.5" data-testid="send-attachments">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Attachments</div>
              <div className="flex items-center gap-2 text-sm text-slate-700 min-w-0"><FileText size={15} className="text-[#0D786A] shrink-0" /> <span className="truncate">{fbase}.pdf</span></div>
              <div className="flex items-center gap-2 text-sm text-slate-700 min-w-0"><FileDown size={15} className="text-[#0D786A] shrink-0" /> <span className="truncate">{fbase}.csv</span></div>
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <Button variant="outline" onClick={() => setSendOpen(false)} className="flex-1 h-11 rounded-xl">Cancel</Button>
            <Button data-testid="send-confirm" onClick={confirmSend} disabled={sending || !recipient} className="flex-1 h-11 rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">
              {sending ? <><Loader2 className="animate-spin mr-2" size={16} /> Sending…</> : "Confirm Send"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}

function Stat({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`font-num font-bold text-lg tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}
